import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";

import axios from "axios";
import Swal from "sweetalert2";

import ActionsMenu from "./ActionsMenu";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// =======================================================
// INITIAL FORM
// Matches core_button_actions Prisma model
// =======================================================

const initialForm = {
  module_button_id: "",
  action_code: "",
  action_name: "",
  http_method: "",
  api_path: "",
  is_active: 1,
};

const ButtonActions = () => {
  const [rows, setRows] = useState([]);

  const [buttons, setButtons] = useState([]);

  const [form, setForm] = useState(initialForm);

  const [openDialog, setOpenDialog] = useState(false);

  const [editId, setEditId] = useState(null);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);

  const [pageSize, setPageSize] = useState(10);

  const [rowCount, setRowCount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // =======================================================
  // USER / TENANT
  // =======================================================

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const clientId = user.client_id;
  const collegeId = user.college_id;

  // =======================================================
  // SNACKBAR
  // =======================================================

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  // =======================================================
  // LOAD ALL MODULE BUTTONS
  // =======================================================

  const loadButtons = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/module-buttons/all/${clientId}/${collegeId}`
      );

      const result = response.data;

      const data = result?.data || result || [];

      setButtons(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("loadButtons error:", error);

      showSnackbar(error.response?.data?.message || "Failed to load buttons", "error");
    }
  };

  // =======================================================
  // LOAD BUTTON ACTIONS
  // =======================================================

  const loadActions = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API_BASE_URL}/api/rbacall/button-actions/${clientId}/${collegeId}`,
        {
          params: {
            page: page + 1,
            pageSize,
            search,
          },
        }
      );

      const result = response.data;

      /*
       * Backend response:
       *
       * {
       *   success: true,
       *   data: {
       *     items: [],
       *     total: 10,
       *     page: 1,
       *     pageSize: 10,
       *     totalPages: 1
       *   }
       * }
       */

      const items = result?.data?.items || result?.items || result?.rows || [];

      const total =
        result?.data?.total ?? result?.total ?? result?.totalCount ?? result?.rowCount ?? 0;

      setRows(
        Array.isArray(items)
          ? items.map((item) => ({
              ...item,
              id: item.button_action_id ?? item.action_id,
            }))
          : []
      );

      setRowCount(Number(total));
    } catch (error) {
      console.error("loadActions error:", error);

      showSnackbar(error.response?.data?.message || "Failed to load button actions", "error");

      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // =======================================================
  // INITIAL LOAD BUTTONS
  // =======================================================

  useEffect(() => {
    if (clientId && collegeId) {
      loadButtons();
    }
  }, [clientId, collegeId]);

  // =======================================================
  // LOAD ACTIONS WHEN PAGINATION / SEARCH CHANGES
  // =======================================================

  useEffect(() => {
    if (clientId && collegeId) {
      loadActions();
    }
  }, [clientId, collegeId, page, pageSize, search]);

  // =======================================================
  // ADD
  // =======================================================

  const handleAdd = () => {
    setEditId(null);

    setForm({
      ...initialForm,
    });

    setOpenDialog(true);
  };

  // =======================================================
  // EDIT
  // =======================================================

  const handleEdit = (row) => {
    setEditId(row.button_action_id ?? row.action_id);

    setForm({
      module_button_id: row.module_button_id ?? row.button_id ?? "",

      action_code: row.action_code || "",

      action_name: row.action_name || "",

      http_method: row.http_method || "",

      api_path: row.api_path || "",

      is_active: row.is_active ? 1 : 0,
    });

    setOpenDialog(true);
  };

  // =======================================================
  // SAVE
  // =======================================================

  const handleSave = async () => {
    try {
      // ---------------------------------------------------
      // VALIDATION
      // ---------------------------------------------------

      if (!form.module_button_id) {
        showSnackbar("Please select button", "error");
        return;
      }

      if (!form.action_code.trim()) {
        showSnackbar("Action code is required", "error");
        return;
      }

      if (!form.action_name.trim()) {
        showSnackbar("Action name is required", "error");
        return;
      }

      // ---------------------------------------------------
      // PAYLOAD
      // ---------------------------------------------------

      const payload = {
        module_button_id: form.module_button_id,

        action_code: form.action_code.trim().toUpperCase(),

        action_name: form.action_name.trim(),

        http_method: form.http_method ? form.http_method.trim().toUpperCase() : "",

        api_path: form.api_path ? form.api_path.trim() : "",

        is_active: Number(form.is_active),

        client_id: clientId,

        college_id: collegeId,
      };

      // ---------------------------------------------------
      // UPDATE
      // ---------------------------------------------------

      if (editId) {
        await axios.put(
          `${API_BASE_URL}/api/rbacall/button-actions/${clientId}/${collegeId}/${editId}`,
          payload
        );

        showSnackbar("Button action updated successfully");
      }

      // ---------------------------------------------------
      // CREATE
      // ---------------------------------------------------
      else {
        await axios.post(
          `${API_BASE_URL}/api/rbacall/button-actions/${clientId}/${collegeId}`,
          payload
        );

        showSnackbar("Button action created successfully");
      }

      // ---------------------------------------------------
      // CLOSE
      // ---------------------------------------------------

      setOpenDialog(false);

      setForm({
        ...initialForm,
      });

      setEditId(null);

      // Reload
      loadActions();
    } catch (error) {
      console.error("handleSave error:", error);

      showSnackbar(error.response?.data?.message || "Failed to save button action", "error");
    }
  };

  // =======================================================
  // DELETE / DEACTIVATE
  // =======================================================

  const handleDelete = async (row) => {
    const actionId = row.button_action_id ?? row.action_id;

    const result = await Swal.fire({
      title: "Delete Button Action?",

      text: `Are you sure you want to delete "${row.action_name}"?`,

      icon: "warning",

      showCancelButton: true,

      confirmButtonText: "Yes, Delete",

      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/rbacall/button-actions/${clientId}/${collegeId}/${actionId}`
      );

      Swal.fire({
        icon: "success",

        title: "Deleted",

        text: "Button action deleted successfully",

        timer: 1500,

        showConfirmButton: false,
      });

      loadActions();
    } catch (error) {
      console.error("handleDelete error:", error);

      Swal.fire({
        icon: "error",

        title: "Error",

        text: error.response?.data?.message || "Failed to delete button action",
      });
    }
  };

  // =======================================================
  // DATA GRID COLUMNS
  // =======================================================

  const columns = [
    {
      field: "sno",

      headerName: "S.No",

      width: 70,

      sortable: false,

      renderCell: (params) => page * pageSize + params.api.getRowIndex(params.id) + 1,
    },

    // ---------------------------------------------------
    // MODULE
    // ---------------------------------------------------

    {
      field: "module_name",

      headerName: "Module",

      flex: 1,

      minWidth: 140,
    },

    // ---------------------------------------------------
    // TAB
    // ---------------------------------------------------

    {
      field: "tab_name",

      headerName: "Tab",

      flex: 1,

      minWidth: 140,
    },

    // ---------------------------------------------------
    // BUTTON
    // ---------------------------------------------------

    {
      field: "button_name",

      headerName: "Button",

      flex: 1,

      minWidth: 160,
    },

    // ---------------------------------------------------
    // ACTION CODE
    // ---------------------------------------------------

    {
      field: "action_code",

      headerName: "Action Code",

      flex: 1,

      minWidth: 130,
    },

    // ---------------------------------------------------
    // ACTION NAME
    // ---------------------------------------------------

    {
      field: "action_name",

      headerName: "Action",

      flex: 1,

      minWidth: 160,
    },

    // ---------------------------------------------------
    // HTTP METHOD
    // ---------------------------------------------------

    {
      field: "http_method",

      headerName: "HTTP Method",

      width: 120,

      renderCell: (params) => params.value || "-",
    },

    // ---------------------------------------------------
    // API PATH
    // ---------------------------------------------------

    {
      field: "api_path",

      headerName: "API Path",

      flex: 1.5,

      minWidth: 220,

      renderCell: (params) => params.value || "-",
    },

    // ---------------------------------------------------
    // STATUS
    // ---------------------------------------------------

    {
      field: "is_active",

      headerName: "Status",

      width: 100,

      renderCell: (params) =>
        params.value ? (
          <Typography color="success.main">Active</Typography>
        ) : (
          <Typography color="error.main">Inactive</Typography>
        ),
    },

    // ---------------------------------------------------
    // ACTIONS
    // ---------------------------------------------------

    {
      field: "actions",

      headerName: "Actions",

      width: 90,

      sortable: false,

      renderCell: (params) => (
        <ActionsMenu
          onEdit={() => handleEdit(params.row)}
          onDelete={() => handleDelete(params.row)}
        />
      ),
    },
  ];

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <Box p={2}>
      <Paper elevation={2}>
        {/* =================================================
            TOOLBAR
        ================================================= */}

        <Toolbar
          sx={{
            display: "flex",

            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Button Actions</Typography>

          <Stack direction="row" spacing={2}>
            {/* SEARCH */}

            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);

                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* ADD */}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{ color: "#fff" }}
            >
              Add Action
            </Button>
          </Stack>
        </Toolbar>

        {/* =================================================
            DATA GRID
        ================================================= */}

        <Box
          sx={{
            height: 600,

            width: "100%",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            page={page}
            pageSize={pageSize}
            rowCount={rowCount}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);

              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      {/* ===================================================
          ADD / EDIT DIALOG
      =================================================== */}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Edit Button Action" : "Add Button Action"}</DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            {/* ============================================
                BUTTON
            ============================================ */}

            <TextField
              select
              label="Button"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={form.module_button_id}
              onChange={(e) =>
                setForm({
                  ...form,

                  module_button_id: e.target.value,
                })
              }
              SelectProps={{
                native: true,
              }}
              required
            >
              <option value="">Select Button</option>

              {buttons.map((item) => (
                <option
                  key={item.module_button_id ?? item.button_id}
                  value={item.module_button_id ?? item.button_id}
                >
                  {item.button_name}
                </option>
              ))}
            </TextField>

            {/* ============================================
                ACTION CODE
            ============================================ */}

            <TextField
              label="Action Code"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={form.action_code}
              onChange={(e) =>
                setForm({
                  ...form,

                  action_code: e.target.value.toUpperCase(),
                })
              }
              required
            />

            {/* ============================================
                ACTION NAME
            ============================================ */}

            <TextField
              label="Action Name"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={form.action_name}
              onChange={(e) =>
                setForm({
                  ...form,

                  action_name: e.target.value,
                })
              }
              required
            />

            {/* ============================================
                HTTP METHOD
            ============================================ */}

            <TextField
              select
              label="HTTP Method"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={form.http_method}
              onChange={(e) =>
                setForm({
                  ...form,

                  http_method: e.target.value,
                })
              }
              SelectProps={{
                native: true,
              }}
            >
              <option value="">Select HTTP Method</option>

              <option value="GET">GET</option>

              <option value="POST">POST</option>

              <option value="PUT">PUT</option>

              <option value="PATCH">PATCH</option>

              <option value="DELETE">DELETE</option>
            </TextField>

            {/* ============================================
                API PATH
            ============================================ */}

            <TextField
              label="API Path"
              fullWidth
              placeholder="/api/example"
              InputLabelProps={{
                shrink: true,
              }}
              value={form.api_path}
              onChange={(e) =>
                setForm({
                  ...form,

                  api_path: e.target.value,
                })
              }
            />

            {/* ============================================
                STATUS
            ============================================ */}

            <TextField
              select
              label="Status"
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              value={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,

                  is_active: Number(e.target.value),
                })
              }
              SelectProps={{
                native: true,
              }}
            >
              <option value={1}>Active</option>

              <option value={0}>Inactive</option>
            </TextField>
          </Stack>
        </DialogContent>

        {/* =================================================
            DIALOG ACTIONS
        ================================================= */}

        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>

          <Button variant="contained" onClick={handleSave} sx={{ color: "#fff" }}>
            {editId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",

          horizontal: "right",
        }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ButtonActions;
